/* eslint-disable @typescript-eslint/restrict-plus-operands */
import dayjs from 'dayjs';
import type { Asset } from 'types/asset.types';
import { convertKpaToPsi, convertCentToFahr } from 'utils/helpers/unitSystem';
import { isOverMax } from './Components/Content/Components/VehicleTypes/util';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
  TpmsLeakageNotification,
} from 'types/tire-pattern.types';
/**
 * Checks if a value is a valid number or numeric string.
 * Returns true for finite numbers and strings that can be converted to valid numbers.
 * Returns false for NaN, Infinity, -Infinity, null, undefined, and non-numeric strings/values.
 *
 * @param value - The value to check
 * @returns True if value is a valid number or numeric string, false otherwise
 *
 * @example
 * isNumber(123)      // true
 * isNumber('123')    // true
 * isNumber('123.45') // true
 * isNumber('abc')    // false
 */
export const isNumber = (value: unknown): boolean => {
  // Handle number type
  if (typeof value === 'number') {
    return !isNaN(value) && Number.isFinite(value);
  }

  // Handle string type
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return (
      trimmed !== '' &&
      !isNaN(Number(trimmed)) &&
      Number.isFinite(Number(trimmed))
    );
  }

  return false;
};

export const tireCodeNames = {
  light_truck: {
    '0x17': 'Front-Left',
    '0x19': 'Front-Right',
    '0x27': 'Rear-Left',
    '0x29': 'Rear-Right',
  },
  heavy_truck: {
    '0x17': 'Front-Left',
    '0x19': 'Front-Right',
    '0x27': 'Rear-Left',
    '0x29': 'Rear-Right',
  },
  dozer: {
    '0x17': 'Front-Left',
    '0x19': 'Front-Right',
    '0x27': 'Rear-Left',
    '0x29': 'Rear-Right',
  },
  loader: {
    '0x17': 'Front-Left',
    '0x19': 'Front-Right',
    '0x27': 'Rear-Left',
    '0x29': 'Rear-Right',
  },
  drill: {
    '0x17': 'Front-Left',
    '0x19': 'Front-Right',
    '0x27': 'Rear-Left',
    '0x29': 'Rear-Right',
  },
  grader: {
    '0x17': 'Front-Left',
    '0x19': 'Front-Right',
    '0x27': 'Middle-Left',
    '0x29': 'Middle-Right',
    '0x37': 'Rear-Left',
    '0x39': 'Rear-Right',
  },
  haul_truck: {
    '0x17': 'Front-Left',
    '0x19': 'Front-Right',
    '0x27': 'Rear-Inside-Left',
    '0x29': 'Rear-Inside-Right',
    '0x26': 'Rear-Outside-Left',
    '0x2A': 'Rear-Outside-Right',
  },
};

export const fixedLimts = {
  imperial: {
    pressure: {
      minValue: 350,
      maxValue: 650,
      startValue: 0,
      endValue: 180,
    },
    temperature: {
      minValue: 40,
      maxValue: 186,
      startValue: 32,
      endValue: 212,
    },
  },
  metric: {
    temperature: {
      minValue: 0,
      maxValue: 100,
      startValue: 0,
      endValue: 100,
    },
    pressure: {
      minValue: 20,
      maxValue: 60,
      startValue: 0,
      endValue: 1200,
    },
  },
};

export function getTireValuesFromSensorData(
  tireNumberSystem: string[],
  data: any[],
  limits: any,
  unitSystem: string,
  make: string,
  model: string,
  metricType: 'pressure' | 'temperature' = 'pressure'
) {
  const result: any = {};

  tireNumberSystem.forEach((code, index) => {
    const tireNumber = index + 1;
    const sensorCode = `0x${code.replace(/^0x/, '').toLowerCase()}`;

    const dataEntry = data.find((entry) =>
      entry?.device_sensor?.toLowerCase().includes(sensorCode)
    );
    const value = Number(dataEntry?.value ?? 'N/A');

    // First try a direct tireCode lookup (used when caller passes a pre-filtered
    // positions array from the axle-wise API, where items don't have make/model).
    // Fall back to the old make+model filter for backward compatibility.
    let tireLimit =
      limits.find(
        (item: any) => item.tireCode === code && item.make === undefined
      ) ??
      limits
        .filter((lmt: any) => lmt.make === make && lmt.model === model)
        .find((item: any) => item.tireCode === code);

    let maxValue, minValue;
    if (metricType === 'pressure') {
      maxValue =
        unitSystem === 'metric'
          ? tireLimit?.maxPressure
          : convertKpaToPsi(tireLimit?.maxPressure);

      minValue =
        unitSystem === 'metric'
          ? tireLimit?.minPressure
          : convertKpaToPsi(tireLimit?.minPressure);
    } else {
      // Temperature limits
      maxValue =
        unitSystem === 'metric'
          ? tireLimit?.maxTemperature
          : convertCentToFahr(tireLimit?.maxTemperature);

      minValue =
        unitSystem === 'metric'
          ? tireLimit?.minTemperature
          : convertCentToFahr(tireLimit?.minTemperature);
    }

    tireLimit = {
      ...tireLimit,
      minValue,
      maxValue,
    };

    result[`p${tireNumber}`] = dataEntry?.ts ? value : null;
    result[`p${tireNumber}Limits`] = tireLimit;
    result[`isP${tireNumber}WarninigMax`] = isOverMax(
      value,
      maxValue,
      minValue
    );
  });

  return result;
}

export function getLastUpdatedTimestamp(data: any[]) {
  if (!Array.isArray(data) || data.length === 0) return 'N/A';

  const timestamps = data
    .map((item) => item?.ts)
    .filter((ts) => typeof ts === 'number' && !isNaN(ts));

  if (timestamps.length === 0) return 'N/A';

  const maxTs = Math.max(...timestamps);
  const updatedTime = dayjs(maxTs);

  return updatedTime?.format('YYYY-MM-DD HH:mm:ss');
}

export function filterNonEmptyArrays(
  data: Record<string, Record<string, any[]>>
): Record<string, Record<string, any[]>> {
  const result: Record<string, Record<string, any[]>> = {};

  for (const [key, value] of Object.entries(data)) {
    const hasNonEmptyArray = Object.values(value).some((arr) => arr.length > 0);

    if (hasNonEmptyArray) {
      result[key] = value;
    }
  }

  return result;
}

export function transformData(data: any) {
  const result: any[] = [];

  for (const asset in data) {
    const tires = data[asset];

    for (const tire in tires) {
      const readings = tires[tire];

      readings.forEach((reading: any) => {
        result.push({
          day: reading.date,
          asset,
          tire,
          average: reading.average,
        });
      });
    }
  }

  return result;
}

export function countTiresInDays(data: any[]) {
  const dayCountMap: any = {};

  data.forEach((item) => {
    const day = item.day;
    if (dayCountMap[day]) {
      dayCountMap[day]++;
    } else {
      dayCountMap[day] = 1;
    }
  });

  return Object.entries(dayCountMap).map(([day, total]) => ({ day, total }));
}

export function getAssetNoOfNotifications(
  notificationsArray: any[],
  bumpersArray: any[]
) {
  const bumperCountMap = notificationsArray.reduce((acc, notification) => {
    const bumperNumber = notification.asset?.bumperNumber;
    if (bumperNumber) {
      acc[bumperNumber] = (acc[bumperNumber] || 0) + 1;
    }
    return acc;
  }, {});

  return bumpersArray.map((bumper) => ({
    ...bumper,
    count: bumperCountMap[bumper.bumperId] || 0,
  }));
}

export function countUniqueTires(array: any[]) {
  const allUniqueKeys = array.flatMap((item) =>
    item.rule.ruleConditionAssociations.map((condition: any) => {
      const parts = condition.tagName?.split('_') || [];
      const tireCode = parts[2] || '';
      return `${tireCode}_${item.asset.bumperNumber}`;
    })
  );

  return new Set(allUniqueKeys).size;
}

/**
 * Normalizes a tireCode to the canonical `0x` + uppercase-hex form used as the
 * key in the `tireCodes` map built by getDynamicAssetTireInfo. Without this,
 * codes containing hex letters (e.g. `0x2a`) fail a direct lookup and fall back
 * to 'N/A' in the exported report, while purely numeric codes (`0x17`) match.
 */
export function normalizeTireCode(tireCode: string): string {
  if (/^0x[0-9a-f]+$/i.test(tireCode)) {
    return '0x' + tireCode.slice(2).toUpperCase();
  }
  return tireCode;
}

/**
 * Title-cases a hyphenated tire position label (e.g. `rear-outside-left` ->
 * `Rear-Outside-Left`) so labels resolved from the raw pattern positions match
 * the formatting used in the tireCodes map built by getDynamicAssetTireInfo.
 */
export function formatTireCodeLabel(label: string): string {
  return label
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');
}

export function getTireName(
  tagName: string,
  tirePatternPositions: TirePatternPosition[]
) {
  const parts = tagName.split('_');
  let tireCode = parts[2] || null;

  if (tireCode) {
    // normalize: always uppercase hex suffix
    tireCode = normalizeTireCode(tireCode);

    return (
      tirePatternPositions.find((item) => item.tireCode === tireCode)
        ?.tireCodeLabel ?? 'N/A'
    );
  } else {
    return 'N/A';
  }
}

/**
 * Gets tire name from tireCode directly (for leakage notifications).
 * Normalizes the tireCode to uppercase hex format before lookup.
 */
export function getTireNameFromCode(
  tireCode: string | null | undefined,
  tirePatternPositions: TirePatternPosition[]
): string {
  if (!tireCode) return 'N/A';

  // normalize: always uppercase hex suffix
  const normalizedCode = normalizeTireCode(tireCode);

  return (
    tirePatternPositions.find((item) => item.tireCode === normalizedCode)
      ?.tireCodeLabel ?? 'N/A'
  );
}

export function updateTireWarningCounts(
  dailySummary: any[],
  notifications: any[]
) {
  const warningCountsByDay = new Map();

  notifications.forEach((notification) => {
    const alertDate = notification.alertStartTs.split('T')[0];

    if (!warningCountsByDay.has(alertDate)) {
      warningCountsByDay.set(alertDate, new Set());
    }

    const tagName =
      notification.rule.ruleConditionAssociations[0]?.tagName || 'unknown';
    const parts = tagName.split('_');
    const tireCode = parts[2] || null;
    const uniqueTireKey = `${notification.deviceId}-${tireCode}`;

    warningCountsByDay.get(alertDate).add(uniqueTireKey);
  });

  return dailySummary.map((day) => {
    const warningSet = warningCountsByDay.get(day.day);
    const warningCount = warningSet ? warningSet.size : 0;

    return {
      ...day,
      tireWarning: warningCount,
      tireOk: day.total - warningCount,
    };
  });
}

export function getRecentNotifications(notifications: any[], days = 7) {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);

  return notifications.filter((notification) => {
    const alertStartDate = new Date(notification.alertStartTs);

    return alertStartDate >= cutoffDate;
  });
}

export function getPreviousNotifications(notifications: any[], days = 7) {
  const now = new Date();

  const endDate = new Date();
  endDate.setDate(now.getDate() - days);

  const startDate = new Date();
  startDate.setDate(now.getDate() - 2 * days);

  return notifications.filter((notification) => {
    const alertStartDate = new Date(notification.alertStartTs);
    return alertStartDate >= startDate && alertStartDate < endDate;
  });
}

/**
 * Counts unique tires from leakage notifications.
 * Leakage notifications have tireCode directly on the object, unlike TPMS notifications
 * which have it in rule.ruleConditionAssociations.
 */
export function countUniqueTiresFromLeakage(leakageNotifications: any[]) {
  const allUniqueKeys = leakageNotifications.map((item) => {
    const tireCode = item.tireCode || '';
    const bumperNumber = item.asset?.bumperNumber || '';
    return `${tireCode}_${bumperNumber}`;
  });

  return new Set(allUniqueKeys).size;
}

/**
 * Gets unique asset IDs from leakage notifications
 */
export function getUniqueAssetsFromLeakage(leakageNotifications: any[]) {
  return [...new Set(leakageNotifications.map((item) => item?.assetId))].length;
}

export function extractTireNotificationsForChart(notifications: any[]) {
  return notifications
    .map((notification) => {
      const fullTagName =
        notification.rule?.ruleConditionAssociations?.[0]?.tagName || null;
      const notificationType = extractReason(notification?.rule?.ruleName);

      let tireCode = null;
      if (fullTagName) {
        const parts = fullTagName.split('_');
        if (parts.length >= 3) {
          tireCode = parts[2];
        }
      }

      const ts = notification.alertStartTs || null;

      return {
        tireCode,
        ts,
        notificationType,
      };
    })
    .filter((item) => item.tireCode && item.ts);
}

export function extractTireLeakageNotificationsForChart(
  notifications: TpmsLeakageNotification[]
) {
  return notifications
    .map((notification) => {
      const notificationType = notification.leakType;
      const tireCode = notification.tireCode;

      const ts = notification.alertStartTs || null;

      return {
        tireCode,
        ts,
        notificationType,
      };
    })
    .filter((item) => item.tireCode && item.ts);
}

export function getRangeDates(id: string) {
  const newEndDate = dayjs();
  let newStartDate = null;

  switch (id) {
    case 'Today':
      newStartDate = dayjs().startOf('day').toDate();
      break;
    case '1D':
      newStartDate = dayjs().subtract(1, 'day').toDate();
      break;
    case '7D':
      newStartDate = dayjs().subtract(7, 'days').toDate();
      break;
    case '14D':
      newStartDate = dayjs().subtract(14, 'days').toDate();
      break;
    case '1M':
      newStartDate = dayjs().subtract(1, 'month').toDate();
      break;
    case '1Y':
      newStartDate = dayjs().subtract(1, 'year').toDate();
      break;
    case 'Custom':
      newStartDate = dayjs().subtract(1, 'day').toDate();
      break;
    default:
      break;
  }

  return { startRangeDate: newStartDate, endRangeDate: newEndDate };
}

export function roundTimestampsToHour(data: any[] = []) {
  if (!data || !Array.isArray(data)) return [];
  return data.map((item) => {
    const date = new Date(item.ts);

    const hourlyDate = new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours()
      )
    );

    const minuteDate = new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes()
      )
    );

    return {
      ...item,
      hourlyTs: hourlyDate.toISOString(),
      minuteTs: minuteDate.toISOString(),
    };
  });
}

export function extractReason(str: string) {
  const knownReasons = [
    'high_pressure',
    'low_pressure',
    'leakage',
    'inflate',
    'low pressure',
    'high pressure',
    'low temperature',
    'high temperature',
    'low_temperature',
    'high_temperature',
  ];
  const lowerStr = str.toLowerCase();

  for (const reason of knownReasons) {
    if (lowerStr.includes(reason)) {
      return reason.replace('_', ' ');
    }
  }

  return 'tpms';
}

export function isNotificationMatch(
  notification: any,
  payload: any,
  lineName: string,
  selectedRange?: string,
  allPayloads?: any[]
) {
  const notificationDate = new Date(notification.ts);
  const payloadDate = new Date(payload.ts);

  const isCorrectTire =
    notification.tireCode?.toLowerCase() === lineName?.toLowerCase();
  if (!isCorrectTire) return false;

  const pointDate = new Date(notification.minuteTs);

  const isExactMinute =
    pointDate.getUTCFullYear() === payloadDate.getUTCFullYear() &&
    pointDate.getUTCMonth() === payloadDate.getUTCMonth() &&
    pointDate.getUTCDate() === payloadDate.getUTCDate() &&
    pointDate.getUTCHours() === payloadDate.getUTCHours() &&
    pointDate.getUTCMinutes() === payloadDate.getUTCMinutes();

  if (isExactMinute) return true;

  // Fuzzy match (handle missing data points)
  if (allPayloads && allPayloads.length > 0) {
    const thresholdMs = 10 * 60 * 1000; // 10 minute window for fuzzy matching
    const diff = Math.abs(notificationDate.getTime() - payloadDate.getTime());

    if (diff <= thresholdMs) {
      const isClosest = allPayloads.every((otherDot) => {
        const otherDiff = Math.abs(notificationDate.getTime() - otherDot.ts);
        if (otherDiff < diff) return false;
        if (otherDiff === diff && otherDot.ts < payloadDate.getTime())
          return false;
        return true;
      });

      if (isClosest) return true;
    }
  }

  return false;
}

export function getPointMatch(
  notifications: any[] = [],
  payload: any,
  lineName: string,
  selectedRange?: string,
  allPayloads?: any[]
) {
  if (!notifications || !Array.isArray(notifications)) return null;

  return notifications.find((notification) =>
    isNotificationMatch(
      notification,
      payload,
      lineName,
      selectedRange,
      allPayloads
    )
  );
}

export function prepareDataForCsv(
  pressureData: any[],
  temperatureData: any[],
  keys: string[],
  tireCodes: any,
  notifications: any[],
  asset: Asset | undefined,
  preferredTimeDisplayMode: any,
  timezone: any,
  fitmentHistory: any[],
  currentPressureData: any,
  currentTemperatureData: any,
  customerId: string,
  makeModelDetails: MakeModelDetails[],
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  },
  selectedRange?: string
) {
  if (!fitmentHistory || !Array.isArray(fitmentHistory)) {
    fitmentHistory = [];
  }
  if (!notifications || !Array.isArray(notifications)) {
    notifications = [];
  }
  const temperatureMap = new Map();

  temperatureData.forEach((arrayGroup, index) => {
    if (
      !arrayGroup ||
      !Array.isArray(arrayGroup) ||
      arrayGroup.length === 0 ||
      index >= keys.length
    ) {
      return;
    }
    const tireCode = keys[index];

    arrayGroup.forEach((obj) => {
      if (obj && typeof obj === 'object') {
        const key = `${obj.ts}-${tireCode}`;
        temperatureMap.set(key, obj.avg_val);
      }
    });
  });
  let tirePositions: TirePatternPosition[] = [];

  if (asset) {
    const assetTireDetails = getDynamicAssetTireInfo(
      makeModelDetails,
      tirePatterns.tirePatternPositions,
      asset.make,
      asset.model,
      customerId
    );
    tirePositions = assetTireDetails.tirePositions;
  }

  const groupedArr = pressureData.flatMap((arrayGroup, index) => {
    if (
      !arrayGroup ||
      !Array.isArray(arrayGroup) ||
      arrayGroup.length === 0 ||
      index >= keys.length
    ) {
      return [];
    }
    const tireCode = keys[index];
    // Prefer the asset's resolved pattern map; fall back to the full pattern
    // position set so sensor codes that exist in the data but not in the
    // (possibly mis-resolved) pattern — e.g. a 6-wheel CAT truck resolving to a
    // 4x4 pattern — still get a real position label instead of 'N/A'.
    const tireCodeName =
      tireCodes[normalizeTireCode(tireCode)] ??
      formatTireCodeLabel(
        getTireNameFromCode(tireCode, tirePatterns.tirePatternPositions)
      );

    return arrayGroup.map((obj) => {
      if (!obj || typeof obj !== 'object') {
        return obj;
      }

      const key = `${obj.ts}-${tireCode}`;
      const temperatureValue = temperatureMap.get(key);

      return {
        ...obj,
        pressureValue: obj.avg_val,
        temperatureValue,
        tireCode,
        tireCodeName,
        bumperNumber: asset?.bumperNumber ?? 'N/A',
      };
    });
  });

  const dataWithNotifications = groupedArr.map((item) => {
    const itemNotification = notifications.filter((notification) =>
      isNotificationMatch(notification, item, item.tireCode, selectedRange)
    );

    const matchingHistory = fitmentHistory.filter((historyItem) => {
      const historyDate = new Date(historyItem.hourlyTs);
      const payloadDate = new Date(item.ts);

      return (
        historyDate.getTime() === payloadDate.getTime() &&
        historyItem.tireCode?.toLowerCase() === item.tireCode?.toLowerCase()
      );
    });

    if (itemNotification.length > 0) {
      const notificationTypeString = [
        ...new Set(
          itemNotification.map((item) => item.notificationType.toUpperCase())
        ),
      ].join(', ');

      if (matchingHistory.length > 0) {
        const swapHistory = matchingHistory.filter(
          (historyMatch: any) => historyMatch.changeType === 'tire-rotated-in'
        );

        const fitmentHistory = matchingHistory.filter(
          (historyMatch: any) => historyMatch.changeType === 'tire-added'
        );

        let notificationText = `(${notificationTypeString})`;

        if (fitmentHistory.length > 0) {
          notificationText += `, Fitment: ${item.tireCodeName} Tire replaced`;
        }

        if (swapHistory.length > 0) {
          const lastHistoryItem = swapHistory[swapHistory.length - 1];
          const swapText = `Swap: ${convertSwapString(
            lastHistoryItem.changeReason,
            tireCodes
          )}`;
          notificationText += `, ${swapText}`;
        }

        return {
          ...item,
          notificationType: notificationText,
        };
      }

      return {
        ...item,
        notificationType: `(${notificationTypeString})`,
      };
    }

    if (matchingHistory.length > 0) {
      const swapHistory = matchingHistory.filter(
        (historyMatch: any) => historyMatch.changeType === 'tire-rotated-in'
      );

      const fitmentHistory = matchingHistory.filter(
        (historyMatch: any) => historyMatch.changeType === 'tire-added'
      );

      let notificationText = '';

      if (fitmentHistory.length > 0) {
        notificationText = `Fitment: ${item.tireCodeName} Tire replaced`;
      }

      if (swapHistory.length > 0) {
        const lastHistoryItem = swapHistory[0];
        const swapText = `Swap: ${convertSwapString(
          lastHistoryItem.changeReason,
          tireCodes
        )}`;

        if (notificationText) {
          notificationText += `, ${swapText}`;
        } else {
          notificationText = swapText;
        }
      }

      return {
        ...item,
        notificationType: notificationText,
      };
    }
    return { ...item, notificationType: '' };
  });

  const now = new Date();

  const noPointNotifications: any[] = notifications.filter((item) => {
    const alertTime = new Date(item.alertStartTs);
    return alertTime > now;
  });

  const notificationDataWithoutPoints = noPointNotifications.map((item) => {
    const parts = (item.rule.ruleConditionAssociations[0]?.tagName ?? '').split(
      '_'
    );
    const tireCode = parts[2] || null;
    return {
      device_sensor: `tpms_tire_${tireCode}_pressure`,
      ts: new Date(item.alertStartTs).getTime(),
      pressureValue: currentPressureData[tireCode] ?? '',
      minPressureValue: currentPressureData[tireCode] ?? '',
      maxPressureValue: currentPressureData[tireCode] ?? '',
      temperatureValue: currentTemperatureData[tireCode] ?? '',
      minTemperatureValue: currentTemperatureData[tireCode] ?? '',
      maxTemperatureValue: currentTemperatureData[tireCode] ?? '',
      tireCodeName: getTireName(
        item.rule.ruleConditionAssociations[0]?.tagName,
        tirePositions
      ),
      bumperNumber: item.asset.bumperNumber,
      notificationType: `Notification: ${extractReason(item?.rule?.ruleName)}`,
    };
  });

  const mergedData = [
    ...dataWithNotifications,
    ...notificationDataWithoutPoints,
  ];

  const formattedTsData = mergedData.map((dataPoint) => {
    return {
      ...dataPoint,
      dateTime: convertUtcToDisplayTime(
        dataPoint.ts,
        preferredTimeDisplayMode,
        timezone,
        'DD MMM YYYY hh:mm:ss A'
      ),
    };
  });

  return formattedTsData;
}

export function generateTpmSNotificationCSV(
  data: any[],
  filename = 'data.csv',
  fields: Array<{ key: string; label: string }> = [],
  title?: string
) {
  if (!data || data.length === 0) {
    return;
  }

  const keys =
    fields.length > 0 ? fields.map((f) => f.key) : Object.keys(data[0]);
  const labels = fields.length > 0 ? fields.map((f) => f.label) : keys;

  const csvHeaders = labels.join(',');

  const csvRows = data.map((obj) => {
    return keys
      .map((key) => {
        let value = obj[key];

        if (key === 'ts' && typeof value === 'number') {
          value = new Date(value).toISOString();
        }

        if (
          typeof value === 'string' &&
          (value.includes(',') || value.includes('"'))
        ) {
          value = `"${value.replace(/"/g, '""')}"`;
        }

        return value ?? '';
      })
      .join(',');
  });

  const lines = [];
  if (title) {
    lines.push(title);
    lines.push('');
  }

  lines.push(csvHeaders, ...csvRows);

  const csvContent = lines.join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return csvContent;
}

export function isWithinLastHour(ts: number) {
  const now = Date.now();
  const oneHourAgo = now - 60 * 60 * 1000;
  return ts >= oneHourAgo && ts <= now;
}

export function formatHistoryTs(
  history: any[] = [],
  data: any,
  timezone: string
) {
  if (!history || !Array.isArray(history)) return [];
  return history.map((item) => {
    const date = new Date(item.changedAt);
    const tireCode = item.tireCode;

    const hourlyDate = new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours()
      )
    );

    let finalHourlyTs = hourlyDate.toISOString();

    if (
      data[tireCode] &&
      Array.isArray(data[tireCode]) &&
      data[tireCode].length > 0
    ) {
      const hourlyTimestamp = hourlyDate.getTime();

      const hourlyMatch = data[tireCode].find(
        (dataPoint: any) => dataPoint.ts === hourlyTimestamp
      );

      if (!hourlyMatch) {
        const { startUTC: historyDayStart, endUTC: historyDayEnd } =
          getStartAndEndOfDayUTC(item.changedAt, timezone);

        const sameDayDataPoint = data[tireCode].find((dataPoint: any) => {
          return (
            dataPoint.ts >= historyDayStart && dataPoint.ts <= historyDayEnd
          );
        });

        if (sameDayDataPoint) {
          finalHourlyTs = new Date(sameDayDataPoint.ts).toISOString();
        }
      }
    }

    return {
      ...item,
      hourlyTs: finalHourlyTs,
    };
  });
}

export function getHistoryMatch(
  history: any[] = [],
  payload: any,
  lineName: string
) {
  if (!history || !Array.isArray(history)) return [];
  const matchingHistory = history.filter((historyItem) => {
    const historyDate = new Date(historyItem.hourlyTs);
    const payloadDate = new Date(payload.ts);

    return (
      historyDate.getTime() === payloadDate.getTime() &&
      historyItem?.tireCode?.toLowerCase() === lineName?.toLowerCase()
    );
  });

  return matchingHistory;
}

export function isSensorEnabled(data: any) {
  try {
    if (!data || typeof data !== 'object') {
      return false;
    }

    const pressureDataEmpty =
      data.pressureData && typeof data.pressureData === 'object'
        ? Object.values(data.pressureData).every(
            (arr) => Array.isArray(arr) && arr.length === 0
          )
        : true;

    const temperatureDataEmpty =
      data.temperatureData && typeof data.temperatureData === 'object'
        ? Object.values(data.temperatureData).every(
            (arr) => Array.isArray(arr) && arr.length === 0
          )
        : true;

    const statusAllNull =
      data.tireCurrentStatus && typeof data.tireCurrentStatus === 'object'
        ? Object.values(data.tireCurrentStatus).every((val) => val === null)
        : true;

    const temperatureAllNull =
      data.tireCurrentTemperature &&
      typeof data.tireCurrentTemperature === 'object'
        ? Object.values(data.tireCurrentTemperature).every(
            (val) => val === null
          )
        : true;

    const timestampInvalid =
      data.latestTireTimeStamp === null ||
      data.latestTireTimeStamp === undefined ||
      data.latestTireTimeStamp === 0;

    return !(
      pressureDataEmpty &&
      temperatureDataEmpty &&
      statusAllNull &&
      temperatureAllNull &&
      timestampInvalid
    );
  } catch (error) {
    console.error('Error checking sensor status:', error);
    return false;
  }
}

export function convertSwapString(reason: string, tireCodes: any) {
  const regex = /^0x[0-9a-fA-F]+ - 0x[0-9a-fA-F]+$/;

  if (!regex.test(reason)) return reason;

  const [code1, code2] = reason.split(' - ');
  const pos1 = tireCodes[code1];
  const pos2 = tireCodes[code2];

  if (!pos1 || !pos2) return reason;

  return `Swapped with ${pos1}`;
}

export function getToolTipText(history: any[] = [], tireCodes: any) {
  if (!history || !Array.isArray(history)) return '';
  const messages = new Set<string>();

  for (const entry of history) {
    if (entry.changeType === 'tire-rotated-in') {
      const rotationText = convertSwapString(entry.changeReason, tireCodes);
      messages.add(rotationText);
    } else if (entry.changeType === 'tire-added') {
      messages.add('Tire Replaced');
    }
  }

  return Array.from(messages).join(' | ');
}

function getStartAndEndOfDayUTC(dateStr: string, timezone: string) {
  const date = new Date(dateStr);

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const d = parts.find((p) => p.type === 'day')?.value;

  const localStartStr = `${y}-${m}-${d}T00:00:00`;
  const localEndStr = `${y}-${m}-${d}T23:59:59.999`;

  const startUTC = new Date(
    new Date(localStartStr).toLocaleString('en-US', { timeZone: timezone })
  ).getTime();
  const endUTC = new Date(
    new Date(localEndStr).toLocaleString('en-US', { timeZone: timezone })
  ).getTime();

  return { startUTC, endUTC };
}

/**
 * Calculate potential fuel savings based on tire pressure notifications
 * @param {Array} notifications - Array of notification objects
 * @param {Object} averageData - Object containing average pressure data by device and tire
 * @param {number} fuelRate - Fuel consumption rate in litres per hour
 * @returns {Object} - Object containing total fuel wasted and breakdown by notification
 */

export function calculatePotentialFuelSavings(
  notifications: any[],
  averageData: any,
  fuelRate: any
) {
  if (!notifications || !Array.isArray(notifications)) {
    return {
      totalFuelWasted: 0,
      totalFuelWastedLitres: 0,
      breakdown: [],
      summary: {
        totalNotificationsProcessed: 0,
        totalTimePeriodHours: 0,
      },
    };
  }
  if (fuelRate === 0) {
    return;
  }
  let totalFuelWasted = 0;
  const fuelWasteBreakdown: any[] = [];

  notifications.forEach((notification) => {
    const ruleName = notification.rule.ruleName.toLowerCase();
    const isLowPressure = ruleName.toLowerCase().includes('low pressure');
    const isHighPressure = ruleName.toLowerCase().includes('high pressure');
    const isTireLeak =
      ruleName.toLowerCase().includes('tire leak') ||
      ruleName.toLowerCase().includes('leakage');
    if (!isLowPressure && !isHighPressure && !isTireLeak) {
      return;
    }

    const deviceId = notification.deviceId;
    const ruleCondition = notification.rule.ruleConditionAssociations[0];

    if (!ruleCondition) {
      return;
    }

    const tagName = ruleCondition.tagName;
    const thresholdValue = ruleCondition.value;

    if (!tagName) {
      return;
    }

    if (thresholdValue === undefined || thresholdValue === null) {
      return;
    }

    const tireCodeMatch = tagName.match(/0x[0-9a-fA-F]+/);
    if (!tireCodeMatch) {
      return;
    }
    const tireCode = tireCodeMatch[0];

    const deviceData = averageData[deviceId];
    if (!deviceData) {
      return;
    }

    const tireData = Object.entries(deviceData).find(
      ([key]) => key.toLowerCase() === tireCode.toLowerCase()
    )?.[1];

    if (!tireData || !Array.isArray(tireData) || tireData.length === 0) {
      return;
    }

    const alertStartTs = new Date(notification.alertStartTs);
    // Skip active alerts — only closed alerts count
    if (!notification.alertEndTs) {
      return;
    }

    const alertEndTs = new Date(notification.alertEndTs);

    const timePeriodMs = alertEndTs.getTime() - alertStartTs.getTime();
    const timePeriodHours = timePeriodMs / (1000 * 60 * 60);

    if (timePeriodHours <= 0) {
      return;
    }

    const relevantPressureData = tireData.filter((dataPoint) => {
      const dataDate = new Date(dataPoint.date);
      return dataDate >= alertStartTs && dataDate <= alertEndTs;
    });

    if (relevantPressureData.length === 0) {
      return;
    }

    const totalPressure = relevantPressureData.reduce(
      (sum, dataPoint) => sum + dataPoint.average,
      0
    );
    const averagePressure = totalPressure / relevantPressureData.length;

    let fuelWasted = 0;
    let pressureDifference = 0;
    let calculationType = '';

    if (isLowPressure || isTireLeak) {
      pressureDifference = thresholdValue - averagePressure;
      if (pressureDifference > 0) {
        fuelWasted = timePeriodHours * pressureDifference * 0.003 * fuelRate;
        calculationType = isLowPressure ? 'Low Pressure' : 'Tire Leak';
      }
    } else if (isHighPressure) {
      pressureDifference = averagePressure - thresholdValue;
      if (pressureDifference > 0) {
        fuelWasted = timePeriodHours * pressureDifference * 0.001 * fuelRate;
        calculationType = 'High Pressure';
      }
    }

    if (fuelWasted > 0) {
      totalFuelWasted += fuelWasted;

      fuelWasteBreakdown.push({
        notificationId: notification.notificationId,
        deviceId,
        tireCode,
        calculationType,
        ruleName: notification.rule.ruleName,
        alertStartTs: notification.alertStartTs,
        alertEndTs: notification.alertEndTs,
        timePeriodHours: parseFloat(timePeriodHours.toFixed(2)),
        averagePressure: parseFloat(averagePressure.toFixed(2)),
        pressureDifference: parseFloat(pressureDifference.toFixed(2)),
        fuelWasted: parseFloat(fuelWasted.toFixed(4)),
        dataPointsUsed: relevantPressureData.length,
      });
    }
  });

  return {
    totalFuelWasted: parseFloat(totalFuelWasted.toFixed(4)),
    totalFuelWastedLitres: parseFloat(totalFuelWasted.toFixed(4)),
    breakdown: fuelWasteBreakdown,
    summary: {
      totalNotificationsProcessed: fuelWasteBreakdown.length,
      totalTimePeriodHours: parseFloat(
        fuelWasteBreakdown
          .reduce((sum, item) => sum + item.timePeriodHours, 0)
          .toFixed(2)
      ),
    },
  };
}

/**
 * Retrieves dynamic tire information for a specific vehicle make/model.
 *
 * This function:
 * 1. Finds the matching make & model record for the given customer.
 * 2. Falls back to a default (where customerId is null) if no direct match is found.
 * 3. Determines the tire pattern name (defaulting to '4x4' if not found).
 * 4. Filters tire positions that match the pattern name.
 * 5. Builds a lookup object (tireCodes) mapping tireCode → formatted tireCodeLabel (capitalized).
 *
 * @param makeModelDetails - Array of all available make/model details.
 * @param tirePatternPositions - Array of all tire pattern positions.
 * @param make - Vehicle make to search for.
 * @param model - Vehicle model to search for.
 * @param customerId - Customer ID for which data should be fetched.
 * @returns An object containing:
 *  - patternName: the detected pattern name.
 *  - tirePositions: the filtered tire pattern positions.
 *  - tireCodes: a lookup of tireCode → formatted label.
 */
export function getDynamicAssetTireInfo(
  makeModelDetails: MakeModelDetails[],
  tirePatternPositions: TirePatternPosition[],
  make: string,
  model: string,
  customerId: string
) {
  let makeModel = makeModelDetails.find(
    (item) =>
      item.make === make &&
      item.model === model &&
      item.customerId === customerId
  );

  if (!makeModel) {
    makeModel = makeModelDetails.find(
      (item) =>
        item.make === make && item.model === model && item.customerId === null
    );
  }

  const patternName = makeModel?.patternName ?? '4x4';

  const tirePositions = (tirePatternPositions ?? []).filter(
    (item) => item.patternName === patternName
  );

  const tireCodes = tirePositions.reduce<Record<string, string>>(
    (acc, { tireCode, tireCodeLabel }) => {
      acc[normalizeTireCode(tireCode)] = formatTireCodeLabel(tireCodeLabel);
      return acc;
    },
    {}
  );

  return {
    patternName,
    tirePositions,
    tireCodes,
  };
}

export function getTireLimits(make: string, model: string, limits: any) {
  const customerLimits =
    limits?.customerLimits?.filter(
      (item: any) => item.make === make && item.model === model
    ) ?? [];

  const globalLimits =
    limits?.globalLimits?.filter(
      (item: any) => item.make === make && item.model === model
    ) ?? [];

  return customerLimits.length > 0 ? customerLimits : globalLimits;
}

/**
 * Orders tire codes based on vehicle pattern to ensure consistent positioning
 * across different views (fleet insight, vehicle detail, etc.)
 *
 * @param dataKeys - Array of tire codes from the data
 * @param itemPattern - Vehicle pattern (e.g., '6x4', '6x6', or '4x4')
 * @returns Array of tire codes ordered by physical position
 */
export const getOrderedTireCodes = (
  dataKeys: string[],
  itemPattern: string
): string[] => {
  const orderedKeys: string[] = [];

  if (itemPattern === '6x4') {
    const positionOrder = [
      '0x17', // Front-Left
      '0x19', // Front-Right
      '0x27', // Rear-Inside-Left
      '0x29', // Rear-Inside-Right
      '0x26', // Rear-Outside-Left
      '0x2A', // Rear-Outside-Right
    ];

    positionOrder.forEach((code) => {
      if (dataKeys.includes(code)) {
        orderedKeys.push(code);
      }
    });
  } else if (itemPattern === '6x6') {
    const positionOrder = [
      '0x17', // Front-Left
      '0x19', // Front-Right
      '0x27', // Middle-Left
      '0x29', // Middle-Right
      '0x37', // Rear-Left
      '0x39', // Rear-Right
    ];

    positionOrder.forEach((code) => {
      if (dataKeys.includes(code)) {
        orderedKeys.push(code);
      }
    });
  } else if (itemPattern === '4x6') {
    const positionOrder = [
      '0x16', // Left-Front-Outside
      '0x17', // Left-Front-Inside
      '0x19', // Right-Front-Inside
      '0x1A', // Right-Front-Outside
      '0x27', // Left-Back
      '0x29', // Right-Back
    ];

    positionOrder.forEach((code) => {
      if (dataKeys.includes(code)) {
        orderedKeys.push(code);
      }
    });
  } else if (itemPattern === '10x6') {
    const positionOrder = [
      '0x17', // Front-Left
      '0x19', // Front-Right
      '0x27', // Axle2-Inside-Left
      '0x29', // Axle2-Inside-Right
      '0x26', // Axle2-Outside-Left
      '0x2A', // Axle2-Outside-Right
      '0x37', // Axle3-Inside-Left
      '0x39', // Axle3-Inside-Right
      '0x36', // Axle3-Outside-Left
      '0x3A', // Axle3-Outside-Right
    ];

    positionOrder.forEach((code) => {
      if (dataKeys.includes(code)) {
        orderedKeys.push(code);
      }
    });
  } else {
    // For four wheels (4x4 or default)
    const positionOrder = [
      '0x17', // Front-Left
      '0x19', // Front-Right
      '0x27', // Rear-Left
      '0x29', // Rear-Right
    ];

    positionOrder.forEach((code) => {
      if (dataKeys.includes(code)) {
        orderedKeys.push(code);
      }
    });
  }

  dataKeys.forEach((code) => {
    if (!orderedKeys.includes(code)) {
      orderedKeys.push(code);
    }
  });

  return orderedKeys;
};

/**
 * Maps a tire code to its position key (p1, p2, etc.) based on the ordered position
 *
 * @param tireCode - The tire code to map (e.g., '0x17')
 * @param itemPattern - Vehicle pattern (e.g., '6x4', '6x6', or '4x4')
 * @param allTireCodes - All available tire codes for the vehicle
 * @returns Position key (e.g., 'p1', 'p2') or empty string if not found
 */
export const getTirePositionKey = (
  tireCode: string,
  itemPattern: string,
  allTireCodes: string[]
): string => {
  const orderedKeys = getOrderedTireCodes(allTireCodes, itemPattern);
  const index = orderedKeys.indexOf(tireCode);
  return index !== -1 ? `p${index + 1}` : '';
};
